import LangEnum from '../../../lang/LangEnum';
import { ErrorResponse, handlerApiError } from '../../../lib/basicRepository';
import { orderProductPacksRepositoryOnline } from '../../../services/modules/order-product-packs/order-product-packs.repository';
import { OrderProductPacksRepositoryProps } from '../../../services/modules/order-product-packs/order-product-packs.types';
import { IPackToUpdate } from '../../orders/products/product/grid/components/packsMenu/types/useHandleOrderProductPacks.types';
import { AvailabilityEnum } from '../../products/ProductTypes';
import { GridItemSkuDTO } from '../../products/details/productDetails.types';
import {
  availabilityToAvailabilityEnum,
  toOfflineAvailability,
} from '../../products/utils/availability.utils';
import { OfflineGridBuilder } from '../builders/offlineGrid/OfflineGrid.builder';
import { OfflinePackStockError } from '../domain/offlineOrder/orderProduct/validator/rules/ValidateProductColorPackStock.rule';
import { OfflineOrderProductStockError } from '../domain/offlineOrder/orderProduct/validator/rules/ValidateStock.rule';
import { OfflineOrderProductAlreadyExists } from '../domain/offlineOrder/utils/errors/OfflineOrderProductAlreadyExists.error';
import { useOfflineOrdersStore } from '../stores/offlineOrders/offlineOrders.store';
import { CreateGridBasedOnPacksParams } from '../stores/offlineOrders/types/offlineOrders.actions.types';
import { createApiSauceResponse } from '../utils/createApiSauceResponse.util';

export const orderProductPacksRepositoryOfflineAdapters: typeof orderProductPacksRepositoryOnline =
  ({
    availability,
    deliveryWindowId,
    orderId,
    productColorId,
  }: OrderProductPacksRepositoryProps) => {
    const updateOrUpdateOrderProductWithPacks = async (
      addProductBasedOnPackParams: CreateGridBasedOnPacksParams,
    ) => {
      try {
        const order = await useOfflineOrdersStore
          .getState()
          .addProductBasedOnPack(orderId, addProductBasedOnPackParams);

        return order;
      } catch (error) {
        if (error instanceof OfflineOrderProductAlreadyExists) {
          const order = await useOfflineOrdersStore.getState().changeProductGridBasedOnPacks({
            orderId,
            packIdsWithMultiplier: addProductBasedOnPackParams.packIdsWithMultiplier,
            orderProductIds: {
              availability: addProductBasedOnPackParams.availability,
              productColorId: addProductBasedOnPackParams.productColorId,
              deliveryWindowId: addProductBasedOnPackParams.deliveryWindowId,
            },
          });

          return order;
        }

        throw error;
      }
    };

    const updatePacks = async (selectedPacks: IPackToUpdate[]) => {
      try {
        const typedDeliveryWindowId = deliveryWindowId ? deliveryWindowId : undefined;
        const typedAvailability = toOfflineAvailability(
          availabilityToAvailabilityEnum(availability),
        );

        const packIdsWithMultiplier = selectedPacks.map((pack) => ({
          packId: pack.id,
          multiplier: pack.quantity,
        }));

        if (typedAvailability === AvailabilityEnum.EMPTY) {
          throw new Error(
            `cannot add packs to an order product with ${AvailabilityEnum.EMPTY} availability`,
          );
        }

        const addProductBasedOnPackParams: CreateGridBasedOnPacksParams =
          typedAvailability !== AvailabilityEnum.READY_FOR_DELIVERY
            ? {
                productColorId,
                packIdsWithMultiplier,
                availability: typedAvailability,
                deliveryWindowId: typedDeliveryWindowId!,
              }
            : {
                productColorId,
                packIdsWithMultiplier,
                availability: typedAvailability,
              };

        const order = await updateOrUpdateOrderProductWithPacks(addProductBasedOnPackParams);

        const offlineGridBuilder = new OfflineGridBuilder({
          productColorId,
          offlineOrder: order,
          availability: typedAvailability,
          priceTableId: order.priceTable.id,
          deliveryWindowId: typedDeliveryWindowId,
        });

        const productDto = await offlineGridBuilder.build();

        return handlerApiError(
          createApiSauceResponse<GridItemSkuDTO[], ErrorResponse>({
            ok: true,
            data: productDto.skus,
          }),
        );
      } catch (error: any) {
        console.error('[updatePacks] error', error);

        const recognizedLangEnums = {
          [OfflineOrderProductStockError.name]: LangEnum.API_EXCEPTION_INSUFFICIENT_INVENTORY,
          [OfflinePackStockError.name]:
            LangEnum.API_EXCEPTION_PRODUCT_COLOR_PACK_STOCK_NOT_ENOUGH_STOCK,
        };

        const langEnum =
          recognizedLangEnums[error.name] || LangEnum.ORDER_PRODUCT_PACK_GENERIC_ERROR_MESSAGE;

        return handlerApiError(
          createApiSauceResponse<GridItemSkuDTO[], ErrorResponse>({
            ok: false,
            data: {
              code: langEnum,
            },
          }),
        );
      }
    };

    return { updatePacks };
  };
